/**
 * deviceType - Centralized participant data-source detection.
 *
 * A participant may contribute data from one or more sources (Fitbit, HealthKit,
 * and potentially more in future). Detection infers from token / authorization
 * presence — the SAME signals the backend `inferDeviceType` (dataCoverage.js)
 * uses. The stored `dataSource` field is intentionally NOT consulted: the web
 * Fitbit flow never maintains it, so it can go stale and disagree with the
 * backend, hiding a participant's real data. (spec B-6)
 *
 * TWO public surfaces:
 *  - SOURCE LIST (preferred for DISPLAY): `getDeviceSources` / `formatDeviceSources`
 *    / `participantHasSource`. Renders "Fitbit, HealthKit" and is EXTENSIBLE — to
 *    add a source, add a detector + ONE `SOURCE_REGISTRY` entry; labels, filtering
 *    and display all derive from it.
 *  - ENUM (kept for LOGIC/API): `getDeviceType` / `deviceTypeMatchesSource` return
 *    the 'fitbit'|'healthkit'|'both'|'none' value the backend metric-coverage API
 *    and the study-level metric selectors consume. Derived from the source list.
 */

/** A single data source a participant can contribute from. Extend here. */
export type DataSource = 'fitbit' | 'healthkit';

/** Enum classification consumed by the backend API + study-level selectors. */
export type DeviceType = 'fitbit' | 'healthkit' | 'both' | 'none';

/**
 * Minimal structural shape needed for device-type detection. Kept loose on
 * purpose so it accepts both the canonical `Participant` type and the local
 * extended shapes used by individual dashboard components.
 */
export interface DeviceTypeParticipant {
  dataSource?: 'fitbit' | 'healthkit' | 'both';
  fitbitTokens?: { accessToken?: string } | null;
  fitbitData?: { accessToken?: string } | null;
  fitbitAccessToken?: string | null;
  fitbitUserId?: string | null;
  fitbitConnected?: boolean | null;
  hasValidFitbit?: boolean | null;
  hasTokens?: boolean | null;
  healthkit?: { authorized?: boolean } | null;
  healthkitConnected?: boolean | null;
  hasValidHealthKit?: boolean | null;
}

export function participantHasFitbit(
  participant: DeviceTypeParticipant
): boolean {
  return !!(
    participant.fitbitTokens?.accessToken ||
    participant.fitbitData?.accessToken ||
    participant.fitbitAccessToken ||
    participant.fitbitUserId ||
    participant.fitbitConnected ||
    participant.hasValidFitbit ||
    participant.hasTokens
  );
}

export function participantHasHealthKit(
  participant: DeviceTypeParticipant
): boolean {
  return !!(
    participant.healthkit?.authorized ||
    participant.healthkitConnected ||
    participant.hasValidHealthKit
  );
}

/**
 * The registry of data sources. THIS is the only place to edit when adding a new
 * source: append `{ id, label, has }`. Order here drives display/iteration order.
 */
const SOURCE_REGISTRY: ReadonlyArray<{
  id: DataSource;
  label: string;
  has: (p: DeviceTypeParticipant) => boolean;
}> = [
  { id: 'fitbit', label: 'Fitbit', has: participantHasFitbit },
  { id: 'healthkit', label: 'HealthKit', has: participantHasHealthKit },
];

/** All known data source ids, in registry order. */
export const DATA_SOURCES: ReadonlyArray<DataSource> = SOURCE_REGISTRY.map(
  (s) => s.id
);

/** Sources a participant currently contributes from (token/auth inference). */
export function getDeviceSources(
  participant: DeviceTypeParticipant
): DataSource[] {
  return SOURCE_REGISTRY.filter((s) => s.has(participant)).map((s) => s.id);
}

/** Human label for a source id ('fitbit' -> 'Fitbit'). */
export function sourceLabel(source: DataSource): string {
  return SOURCE_REGISTRY.find((s) => s.id === source)?.label ?? source;
}

/** Display string: 'Fitbit, HealthKit' | 'Fitbit' | 'None'. Replaces "Both". */
export function formatDeviceSources(participant: DeviceTypeParticipant): string {
  const labels = getDeviceSources(participant).map(sourceLabel);
  return labels.length ? labels.join(', ') : 'None';
}

/** True when the participant contributes from `source`. */
export function participantHasSource(
  participant: DeviceTypeParticipant,
  source: DataSource
): boolean {
  return SOURCE_REGISTRY.some((s) => s.id === source && s.has(participant));
}

/**
 * Enum classification for LOGIC/API (backend metric-coverage + study-level
 * selectors). Derived from the source list: more than one source -> 'both'. The
 * stored `dataSource` is NOT consulted (see header).
 */
export function getDeviceType(participant: DeviceTypeParticipant): DeviceType {
  const sources = getDeviceSources(participant);
  if (sources.length > 1) return 'both';
  return (sources[0] as DeviceType) ?? 'none';
}

/**
 * True when a participant with the given device type should be included for a
 * single-source selection. A 'both' participant matches BOTH 'fitbit' and
 * 'healthkit' selections; an exact match always counts.
 */
export function deviceTypeMatchesSource(
  deviceType: DeviceType,
  selected: 'fitbit' | 'healthkit'
): boolean {
  return deviceType === selected || deviceType === 'both';
}
